'use client';

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { InventoryItem } from '@/lib/services/inventory-items/types';
import { useDraggableItem } from '../hooks/use-draggable-item';
import {
  InventoryItemRow,
  type InventoryRowStruckParts
} from './inventory-item-row';

interface DraggableInventoryItemRowProps {
  item: InventoryItem;
  onClick?: (item: InventoryItem) => void;
  /** Trailing per-row actions — forwarded to `InventoryItemRow`. */
  trailing?: ReactNode;
  /** The expanded detail block — forwarded to `InventoryItemRow` (VALE-1181). */
  expanded?: ReactNode;
  /** The AI-review change badge — forwarded to `InventoryItemRow` (VALE-1181). */
  infoBadge?: ReactNode;
  /** Classes for the whole item — forwarded to `InventoryItemRow` (VALE-1181). */
  itemClassName?: string;
  /** Wrap the row's own line — forwarded to `InventoryItemRow` (VALE-1181). */
  rowWrapper?: (row: ReactNode) => ReactNode;
  /**
   * Whether the row states a figure — forwarded to `InventoryItemRow`
   * (VALE-1363).
   *
   * Forwarded rather than left to the default because the prop it replaces was
   * NOT: `showCurrentValue` was never a prop here, so `BalanceSheetSection`
   * handed it to a row that dropped it on the floor whenever dragging was on.
   * That was invisible only because the one caller wanting a second column was
   * also the one caller with no drag.
   */
  showValue?: boolean;
  /** Struck parts of the row line — forwarded to `InventoryItemRow` (VALE-1181). */
  struck?: InventoryRowStruckParts;
}

export function DraggableInventoryItemRow({
  item,
  onClick,
  trailing,
  expanded,
  infoBadge,
  itemClassName,
  rowWrapper,
  showValue,
  struck
}: DraggableInventoryItemRowProps) {
  const { dragRef, isDragging, attributes, listeners, transform } =
    useDraggableItem({
      itemId: item.id,
      isNonEstate: item.isNonEstate === true
    });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: isDragging ? 50 : undefined
      }
    : undefined;

  return (
    <div
      ref={dragRef}
      style={style}
      {...attributes}
      {...listeners}
      className={cn(
        'cursor-grab touch-none',
        isDragging && 'cursor-grabbing opacity-50'
      )}
    >
      <InventoryItemRow
        item={item}
        onClick={onClick}
        trailing={trailing}
        expanded={expanded}
        infoBadge={infoBadge}
        itemClassName={itemClassName}
        rowWrapper={rowWrapper}
        showValue={showValue}
        struck={struck}
      />
    </div>
  );
}
